import axios from "axios";
import { reactive } from "vue";
import defaultImage from "../components/icons/Header.png";
import defaultJobs from "../components/icons/jobs-img.png";
import defaultSale from "../components/icons/sales-img.png";
import defaultEvents from "../components/icons/Events-img.png";
import defaultServices from "../components/icons/services-img.png";
import { EventBus } from "./EventBus";
import { getCachedCoords, setCachedCoords } from "../utils/geocodeCache";

// ── In-memory cache for service images (filepath → object URL) ─────────────
const _serviceImageCache = new Map();

// Category-specific placeholders shown while real images load or when a
// listing has none. Rentals keeps the existing Header.png; other categories
// get themed SVGs so the card conveys context at a glance.
function placeholderFor(serviceType1) {
  const t = (serviceType1 || "").toLowerCase();
  if (t === "jobs") return defaultJobs;
  if (t === "sale") return defaultSale;
  if (t === "events") return defaultEvents;
  if (t === "services") return defaultServices;
  return defaultImage;
}

export async function getDashboardData({
  userinput = "",
  selectedServiceType = "",
  selectedSubType = "",
  selectedServiceInfo = "",
  selectedDistanceValue = "",
  page = 1,
  perPage = 12,
  // Newest-first unless a caller asks for something else. Landing pages
  // (rentals/jobs/sale/events/services) pass no sortBy at all, so without this
  // they fell through to the API's own default ordering.
  sortBy = "latest",
  preferredCountryName = "",
  // ── Optional: skip geocoding when caller already has coordinates ──
  latOverride = null,
  lngOverride = null,
  // ── Optional: state code for geo-fallback when posts lack coordinates ──
  stateCode = null,
   // ── Optional: when true, restrict results to listings with at least one image ──
  withPhotosOnly = false,
  // Abort signal so the caller can cancel this request (and its in-flight image
  // loads) when a newer fetch supersedes it — prevents stale Photos Only /
  // All Listings responses from overwriting the current toggle state.
  signal = null,
} = {}) {
  try {
    // ── Step 1: Resolve coordinates ─────────────────────────────────────────
    // If lat/lng are supplied directly (e.g. from browser geolocation or a
    // saved profile) use them straight away — no geocoding round-trip needed.
    let lat = latOverride || "";
    let lng = lngOverride || "";

    if (!lat && userinput && userinput.trim() !== "") {
      const cached = getCachedCoords(userinput);
      if (cached) ({ lat, lng } = cached);
    }

    if (!lat && userinput && userinput.trim() !== "") {
      try {
        const { data: autocompleteData } = await axios.post(
          "/api/places/autocomplete",
          { input: userinput },
          { signal }
        );
        const placeID = autocompleteData[0]?.place_id;
        if (placeID) {
          const { data: detailsData } = await axios.post(
            "/api/placedetails",
            { placeId: placeID },
            { signal }
          );
          lat = detailsData.result?.geometry?.location?.lat || detailsData.lat;
          lng = detailsData.result?.geometry?.location?.lng || detailsData.lng;
          setCachedCoords(userinput, lat, lng);
        }
      } catch (err) {
        if (axios.isCancel?.(err) || err?.name === "CanceledError") throw err;
        if (import.meta.env.DEV) {
          console.warn("⚠️ Autocomplete/Details fetch failed:", err.message);
        }
      }
    }

    // ── Step 2: Build category params ──────────────────────────────────────
    // Prefer the subtype when one is selected — it is the more specific filter
    // and every serviceType2 value is unique to a single category, so sending
    // serviceType2 alone scopes results correctly. Sending the parent category
    // too would AND to zero results when the chip and dropdown disagree
    // (e.g. a "Sale" chip + a Jobs subtype). Fall back to the parent category
    // only when no subtype is selected.
    // serviceTypeInfo (the 3rd-level leaf) is the most specific scope, so prefer
    // it over serviceType2/serviceType1 when a leaf was selected. setServiceInfo()
    // already clears the parent category/subtype, so only one is ever set.
    let serviceType1 = "";
    let serviceType2 = "";
    let serviceTypeInfo = "";
    if (selectedServiceInfo && selectedServiceInfo.trim() !== "") {
      serviceTypeInfo = selectedServiceInfo;
    } else if (selectedSubType && selectedSubType.trim() !== "") {
      serviceType2 = selectedSubType;
    } else if (selectedServiceType && selectedServiceType.trim() !== "") {
      serviceType1 = selectedServiceType;
    }

    const params = {
      serviceType1,
      serviceType2,
      serviceTypeInfo,
      distance: selectedDistanceValue?.value || "",
      page,
      limit: perPage,
      sortBy,
      country: preferredCountryName,
    };
    if (lat) params.lat = lat;
    if (lng) params.lng = lng;
    if (stateCode) params.stateCode = stateCode;
    if (withPhotosOnly) params.withPhotosOnly = true;

    // ── Step 3: Fetch paginated listings ───────────────────────────────────
    const { data: nearbyData } = await axios.post("/api/nearbyposts", params, { signal });

    // ── Step 4: Shape services with placeholder images right away ──────────
    let services = (nearbyData.results || [])
      .filter((s) => s.isActive === true || s.isActive === "true")
      .map((s) => ({
        ...s,
        imageUrl: placeholderFor(s.serviceType1),            // shown instantly
        userDetails: s.userDetails || { userName: "Unknown User" },
      }));

    // ── Step 5: Return immediately so the grid renders without waiting ─────
    // Images load in the background and update reactively via loadServiceImages.
    // Pass the same signal so a superseding fetch cancels these image fetches —
    // otherwise the previous toggle's image loads compete with the new toggle's
    // loads and make switching feel slow.
    // Wrap in reactive() BEFORE the image loader keeps its references: it
    // assigns imageUrl after an await, and a write to the raw object bypasses
    // Vue 3's proxy, so the card kept its placeholder until something else
    // re-rendered it (images only appeared after back-from-detail, when the
    // blob cache made every assignment synchronous). reactive() returns the
    // same proxy the component ends up holding, so each write updates the card.
    services = reactive(services);
    loadServiceImages(services, signal);   // fire-and-forget — updates imageUrl in-place

    return {
      services,
      allservicesCount: nearbyData,
      // Return the coords that actually produced these results so the caller
      // can persist them — critical for back-from-detail to rehydrate the
      // same geo scope even when the original filters had no explicit
      // latOverride (e.g. typed-location hero search resolved via autocomplete).
      resolvedLat: lat || null,
      resolvedLng: lng || null,
    };
  } catch (error) {
    if (axios.isCancel?.(error) || error?.name === "CanceledError") throw error;
    console.error("❌ Error fetching dashboard data:", error.message || error);
    throw error;
  }
}

// ── Full-text search across listings ─────────────────────────────────────────
export async function searchServices({
  query = "",
  serviceType1 = "",
  serviceType2 = "",
  page = 1,
  perPage = 12,
  sortBy = "latest",
  signal = null,
} = {}) {
  try {
    const { data } = await axios.post(
      "/api/search",
      { query, serviceType1, serviceType2, page, limit: perPage, sortBy },
      { signal }
    );

    let services = (data.results || [])
      .filter((s) => s.isActive === true || s.isActive === "true")
      .map((s) => ({
        ...s,
        imageUrl: placeholderFor(s.serviceType1),
        userDetails: s.userDetails || { userName: "Unknown User" },
      }));

    services = reactive(services); // see getDashboardData
    loadServiceImages(services, signal);

    return {
      services,
      allservicesCount: data,
    };
  } catch (error) {
    if (axios.isCancel?.(error) || error?.name === "CanceledError") throw error;
    console.error("❌ Error in searchServices:", error.message || error);
    throw error;
  }
}

// ── AI-powered search across listings ────────────────────────────────────────
export async function aiSearchServices({
  query = "",
  page = 1,
  perPage = 12,
  sortBy = "latest",
  signal = null,
  lat = null,
  lng = null,
  distance = null,
} = {}) {
  try {
    const body = { query, page, limit: perPage, sortBy };
    // Geocoded "Where" from the hero dual-input — backend switches to $geoNear
    if (lat != null && lng != null) {
      body.lat = lat;
      body.lng = lng;
      if (distance != null) body.distance = distance;
    }
    const { data } = await axios.post("/api/ai-search", body, { signal });

    let services = (data.results || [])
      .filter((s) => s.isActive === true || s.isActive === "true")
      .map((s) => ({
        ...s,
        imageUrl: placeholderFor(s.serviceType1),
        userDetails: s.userDetails || { userName: "Unknown User" },
      }));

    services = reactive(services); // see getDashboardData
    loadServiceImages(services, signal);

    return {
      services,
      allservicesCount: data,
      parsedIntent: data.parsedIntent || null,
    };
  } catch (error) {
    if (axios.isCancel?.(error) || error?.name === "CanceledError") throw error;
    console.error("❌ Error in aiSearchServices:", error.message || error);
    throw error;
  }
}

// ── Empty-state suggestions (nearby cities, popular categories, recent listings)
export async function getEmptyStateSuggestions({
  lat,
  lng,
  serviceType1 = "",
  serviceType2 = "",
  withPhotosOnly = false,
  preferredCountryName = "",
} = {}) {
  try {
    // Pass through the same filters the dashboard search uses — otherwise the
    // suggestion counts include listings the user can't see (no-photo / wrong
    // country) and clicking through lands on "No listings found" again.
    const res = await axios.post("/api/empty-state-suggestions", {
      lat, lng, serviceType1, serviceType2,
      withPhotosOnly, preferredCountryName,
    });
    const data = res.data || {};

    // The backend returns recentNearby[i].imageUrl as a raw filename from the
    // serviceimage collection. Resolve each one to a blob object URL via the
    // same /api/file/get flow used by the main listings BEFORE returning, so
    // Vue sees fully-resolved URLs (or null → defaultImage) in one render pass.
    // We can't mutate after return because the closure holds the raw object
    // reference, not the reactive proxy Vue creates when the caller assigns.
    if (Array.isArray(data.recentNearby) && data.recentNearby.length) {
      await loadSuggestionImages(data.recentNearby);
    }

    return data;
  } catch (err) {
    console.warn("Failed to fetch empty-state suggestions:", err.message);
    return {};
  }
}

// Mutates each suggestion's imageUrl (raw filename → blob URL). Reuses the
// shared _serviceImageCache so we don't re-download images already loaded
// elsewhere on the page.
async function loadSuggestionImages(items) {
  await Promise.all(
    items.map(async (item) => {
      const filepath = item.imageUrl;
      if (!filepath || typeof filepath !== "string") {
        item.imageUrl = null; // let the template fall back to defaultImage
        return;
      }

      if (_serviceImageCache.has(filepath)) {
        item.imageUrl = _serviceImageCache.get(filepath);
        return;
      }

      try {
        const fileRes = await axios.post(
          "/api/file/get",
          { fileName: filepath, albumName: "default" },
          { responseType: "blob" }
        );

        if (!fileRes.data || fileRes.data.size === 0) {
          item.imageUrl = null;
          return;
        }

        const contentType =
          fileRes.data.type || fileRes.headers["content-type"] || "image/jpeg";
        if (!contentType.startsWith("image/")) {
          item.imageUrl = null;
          return;
        }

        const blob = new Blob([fileRes.data], { type: contentType });
        if (blob.size === 0) {
          item.imageUrl = null;
          return;
        }

        const objectUrl = URL.createObjectURL(blob);
        _serviceImageCache.set(filepath, objectUrl);
        item.imageUrl = objectUrl;
      } catch {
        item.imageUrl = null; // fall back to defaultImage in the template
      }
    })
  );
}

// ── Background image loader ─────────────────────────────────────────────────
// Mutates each service's imageUrl in-place so Vue's reactivity picks it up.
// Uses an in-memory cache to skip repeated fetches for the same file.
// Accepts an AbortSignal so callers can cancel stale image loads when a new
// fetch supersedes this one (e.g. rapid Photos Only / All Listings toggles).
async function loadServiceImages(services, signal = null) {
  await Promise.all(
    services.map(async (service) => {
      if (signal?.aborted) return;

      const activeImage = (service.images || []).find(
        (img) => img.isActive === true || img.isActive === "true"
      );
      if (!activeImage) return;

      // Crawler-imported records store the real image at sourceUrl and write a
      // placeholder filepath (default_<uuid>.jpg) that never lands on disk —
      // so /api/file/get would 404 and the card kept its default placeholder.
      // When sourceUrl is present, render it directly; the browser handles
      // its own caching and we skip the round-trip entirely.
      if (activeImage.sourceUrl) {
        service.imageUrl = activeImage.sourceUrl;
        return;
      }

      if (!activeImage.filepath) return;
      const filepath = activeImage.filepath;

      // Return cached blob URL instantly
      if (_serviceImageCache.has(filepath)) {
        service.imageUrl = _serviceImageCache.get(filepath);
        return;
      }

      try {
        const fileRes = await axios.post(
          "/api/file/get",
          { fileName: filepath, albumName: "default" },
          { responseType: "blob", signal }
        );

        if (signal?.aborted) return;
        if (!fileRes.data || fileRes.data.size === 0) return;

        const contentType =
          fileRes.data.type || fileRes.headers["content-type"] || "image/jpeg";
        if (!contentType.startsWith("image/")) return;

        const blob = new Blob([fileRes.data], { type: contentType });
        if (blob.size === 0) return;

        const objectUrl = URL.createObjectURL(blob);
        _serviceImageCache.set(filepath, objectUrl);
        service.imageUrl = objectUrl;         // Vue reactivity updates the card
      } catch {
        // leave the placeholder — no console noise in production
      }
    })
  );
}
